import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { Badge } from '@mui/material';
import { gpsStatusBadgeColor } from 'utils/constant';

const LinkedDeviceDetailPanel = ({ linked_devices, deviceType }) => {
    // Extract linked devices from row data
    const linkedDevices = linked_devices || [];

    if (!linkedDevices || linkedDevices.length === 0) {
        return (
            <MDBox sx={{ p: 3, textAlign: 'center' }}>
                <MDTypography variant="body2" sx={{ color: '#9ca3af' }}>
                    No linked devices found
                </MDTypography>
            </MDBox>
        );
    }

    // Define columns based on device type
    const getColumns = () => {
        const baseColumns = [
            {
                field: 'device_name',
                headerName: 'Device Name',
                width: 200,
                renderCell: ({ row }) => (
                    <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                        {row?.device_data?.name}
                    </MDTypography>
                )
            },
            {
                field: 'device_id',
                headerName: 'ID',
                width: 80,
                renderCell: ({ value }) => (
                    <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                        {value}
                    </MDTypography>
                )
            }
        ];

        // Asset Tracker (type 8) - show pressure, level, volume, lat, long
        if (deviceType === 8) {
            return [
                ...baseColumns,
                {
                    field: 'pressure_psi',
                    headerName: 'Pressure (PSI)',
                    width: 140,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.device_value?.channels?.[10]?.current_value ?? 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'level_percent',
                    headerName: 'Level (%)',
                    width: 120,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.device_value?.channels?.[9]?.current_value ? `${row?.device_value.channels[9].current_value}%` : 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'volume_gallons',
                    headerName: 'Volume (Gal)',
                    width: 140,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.device_value?.channels?.[9]?.volume_level ? `${row?.device_value.channels[9].volume_level.toFixed(2)}` : 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'latitude',
                    headerName: 'Latitude',
                    width: 120,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.entity_address_item?.latitude ?? 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'longitude',
                    headerName: 'Longitude',
                    width: 120,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.entity_address_item?.longitude ?? 'N/A'}
                        </MDTypography>
                    )
                }
            ];
        }

        // HolyKel (type 9) - show moment_status, speed, heading, lat, long
        if (deviceType === 9) {
            return [
                ...baseColumns,
                {
                    field: 'moment_status',
                    headerName: 'Status',
                    width: 120,
                    renderCell: ({ row }) => (
                        <Badge
                            color={gpsStatusBadgeColor(row?.device_value?.moment_status)}
                            variant={'standard'}
                            badgeContent={row?.device_value?.moment_status || 'N/A'}
                            style={{ marginLeft: "30px" }}
                        />
                    )
                },
                {
                    field: 'speed',
                    headerName: 'Speed',
                    width: 100,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.device_value?.convert_position_speed ?? 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'heading',
                    headerName: 'Heading',
                    width: 100,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.device_value?.position_direction ?? 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'location',
                    headerName: 'Location',
                    width: 300,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.geofences?.name ?? row?.device_value?.reverse_geocoded_address?.formatted_address ?? 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'latitude',
                    headerName: 'Latitude',
                    width: 120,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.device_value?.position_latitude ?? 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'longitude',
                    headerName: 'Longitude',
                    width: 120,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.device_value?.position_longitude ?? 'N/A'}
                        </MDTypography>
                    )
                }
            ];
        }

        // Level Monitor (type 4) - similar to Asset Tracker
        if (deviceType === 4) {
            return [
                ...baseColumns,
                {
                    field: 'pressure_psi',
                    headerName: 'Pressure (PSI)',
                    width: 140,
                    renderCell: ({ value }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {value ?? 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'level_percent',
                    headerName: 'Level (%)',
                    width: 120,
                    renderCell: ({ value }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {value ? `${value}%` : 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'volume_gallons',
                    headerName: 'Volume (Gal)',
                    width: 140,
                    renderCell: ({ value }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {value ? `${value.toFixed(2)}` : 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'latitude',
                    headerName: 'Latitude',
                    width: 120,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.location?.latitude ?? 'N/A'}
                        </MDTypography>
                    )
                },
                {
                    field: 'longitude',
                    headerName: 'Longitude',
                    width: 120,
                    renderCell: ({ row }) => (
                        <MDTypography variant="span" sx={{ fontSize: '0.8rem' }}>
                            {row?.location?.longitude ?? 'N/A'}
                        </MDTypography>
                    )
                }
            ];
        }

        return baseColumns;
    };

    // Transform linked devices data for DataGrid
    const rows = linkedDevices.map((device, index) => ({
        id: device.device_id || index,
        ...device
    }));

    return (
        <MDBox sx={{ p: 1, backgroundColor: '#1a2035' }}>
            <DataGrid
                rows={rows}
                columns={getColumns()}
                autoHeight
                hideFooter
                disableSelectionOnClick
                disableColumnMenu
                sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid #344767',
                        color: '#fff'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#202940',
                        borderBottom: '1px solid #344767',
                        color: '#fff'
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#fff',
                        fontWeight: 600
                    },
                    '& .MuiDataGrid-row': {
                        backgroundColor: '#202940',
                        '&:hover': {
                            backgroundColor: '#2a3447'
                        }
                    }
                }}
            />
        </MDBox>
    );
};

export default LinkedDeviceDetailPanel;
